import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Button, Card, Row, Col, Alert } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ISO6391 from "iso-639-1";
import { Upload, X } from "lucide-react";
import { BiEdit, BiRedo, BiLaptop, BiMobile } from "react-icons/bi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../utils/api";
import Breadcrumb from "../../components/Common/Bredcrump";
import StreamingContentRenderer from "../../components/Common/StreamingContentRenderer";
import Loader from "../../components/Common/Loader";
import { uploadCompanyKnowledgeFiles } from "../../utils/Product";
import DynamicAgentForm from "../../components/DiyAgent/DynamicAgentForm";
import { executeDiyAgent } from "../../services/diyAgentService";
import { saveContentPostDraft, updateContentPostDraft, getContentPostById } from "../../services/contentPostService";
import ContentPostDraftModal from "../../components/DiyAgent/ContentPostDraftModal";
import { EmailSectionProvider, useEmailSections } from "../../components/Context/EmailSectionContext";
import EmailDetailsContainer from "../../components/Email/EmailDetailsContainer";
import EmailHeaderImage from "../../components/Email/EmailHeaderImage";
import { useCompany } from "../../context/CompanyContext";
import "./FreeFormContent.css";

/**
 * Dynamic Playground for DIY Agents
 * 
 * This component creates a new playground screen for DIY agents.
 * When user clicks Generate, it calls the API and renders content
 * based on the response (freeform, blog sections, email sections, etc.)
 */
const DiyAgentPlayground = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { agentName } = params;
  const { companyId, companyKnowledgeDocs } = useCompany();
  
  // Get agent data from location state
  const [agentData, setAgentData] = useState(location.state?.agentData || null);
  const [loading, setLoading] = useState(!agentData);
  const [error, setError] = useState(null);

  // Form state for user input - will be initialized dynamically when agent data loads
  const [formData, setFormData] = useState({
    purpose: location.state?.purpose || "", // Static required field
    title: location.state?.title || "",
    prompt: location.state?.prompt || "",
    language: location.state?.language ? {
      label: location.state.language,
      value: location.state.language,
    } : null,
    locationFocus: location.state?.locationFocus ? {
      label: location.state.locationFocus,
      value: location.state.locationFocus,
    } : null,
    role: location.state?.role || "",
    goal: location.state?.goal || "",
    constraints: location.state?.constraints || "",
  });

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(location.state?.image || "");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);

  // Location options
  const [allLocations, setAllLocations] = useState([]);

  // Content generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasFirstChunk, setHasFirstChunk] = useState(false);
  const [isStreamingDone, setIsStreamingDone] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [parsedHTMLContent, setParsedHTMLContent] = useState("");
  const [finalContent, setFinalContent] = useState("");
  const [generatedData, setGeneratedData] = useState(null);
  const [fullJsonResponse, setFullJsonResponse] = useState(null); // Store full JSON response from API
  const [contentType, setContentType] = useState(null); // 'freeform', 'blog', 'email'
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [initialContent, setInitialContent] = useState("");

  // Blog section editing state
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editSectionText, setEditSectionText] = useState("");
  const [blogSections, setBlogSections] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Email sections and style state
  const [emailSections, setEmailSections] = useState([]);
  const [emailData, setEmailData] = useState({});
  const emailSectionsRef = useRef([]); // Ref to store latest sections for saving
  const [primaryColor, setPrimaryColor] = useState("#333333");
  const [secondaryColor, setSecondaryColor] = useState("#a1894c");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [fontFamily, setFontFamily] = useState("helvetica, 'helvetica neue', arial, verdana, sans-serif");
  const [previewMode, setPreviewMode] = useState("desktop");

  // Draft state
  const [savedContentId, setSavedContentId] = useState(null);
  const [isContentSaved, setIsContentSaved] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftTitle, setDraftTitle] = useState(formData.title || ""); // Shared title variable

  // Get data from location state
  const { tags, documents, uid, uploadedFiles } = location.state || {};

  // Update draftTitle when formData.title changes
  useEffect(() => {
    if (formData.title) {
      setDraftTitle(formData.title);
    }
  }, [formData.title]);

  // Language options
  const languageOptions = ISO6391.getAllNames().map(name => ({
    value: name,
    label: name,
  }));

  // Load location options for AsyncSelect
  const loadLocationOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback([]);
      return;
    }
    const filteredLocations = allLocations.filter(location =>
      location.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filteredLocations);
  };

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/listing?type=country');
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const locationOptions = response.data.data.map(item => ({
            value: item.name,
            label: item.name,
          }));
          setAllLocations(locationOptions);
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    };
    fetchLocations();
  }, []);

  // Fetch content post by ID if in edit mode
  useEffect(() => {
    const fetchContentPost = async () => {
      const postId = location.state?.postId;
      if (postId && location.state?.editMode) {
        try {
          setLoading(true);
          const response = await getContentPostById(postId);
          
          if (response && response.content_post) {
            const post = response.content_post;
            
            // Set agent data if provided
            if (location.state?.agentData) {
              setAgentData(location.state.agentData);
            }
            
            // Parse metafields to get variables
            let savedVariables = {};
            if (post.metafields) {
              try {
                savedVariables = typeof post.metafields === 'string' 
                  ? JSON.parse(post.metafields) 
                  : post.metafields;
              } catch (e) {
                console.error('Error parsing metafields:', e);
                savedVariables = {};
              }
            }
            
            // Set form data from post, including variables from metafields
            setFormData(prev => {
              const updatedData = {
                ...prev,
                title: post.title || prev.title,
                prompt: '', // Keep prompt empty when editing
              };
              
              // Populate dynamic variables from metafields
              Object.keys(savedVariables).forEach(key => {
                const value = savedVariables[key];
                
                // Convert string values to objects for select fields
                if (typeof value === 'string') {
                  // Check if this is a language field - convert to object
                  if (key.toLowerCase().includes('language') || key.toLowerCase().includes('lang')) {
                    const languageOptions = ISO6391.getAllNames().map(name => ({
                      value: name,
                      label: name,
                    }));
                    const matchedOption = languageOptions.find(opt => opt.value === value || opt.label === value);
                    updatedData[key] = matchedOption || { value: value, label: value };
                  }
                  // Check if this is a location field - convert to object
                  else if (key.toLowerCase().includes('location')) {
                    // Try to find in allLocations, otherwise create object
                    const matchedLocation = allLocations.find(loc => loc.value === value || loc.label === value);
                    updatedData[key] = matchedLocation || { value: value, label: value };
                  }
                  // For other fields, keep as string
                  else {
                    updatedData[key] = value;
                  }
                } else {
                  // Already an object, use as-is
                  updatedData[key] = value;
                }
              });
              
              return updatedData;
            });
            
            // Set draft title
            setDraftTitle(post.title || "");
            
            // Parse and set generated content
            if (post.generated_content) {
              try {
                // generated_content is a JSON string, parse it
                const parsedContent = typeof post.generated_content === 'string' 
                  ? JSON.parse(post.generated_content) 
                  : post.generated_content;
                
                // Store full JSON response
                setFullJsonResponse(typeof post.generated_content === 'string' 
                  ? post.generated_content 
                  : JSON.stringify(post.generated_content));
                
                setGeneratedData(parsedContent);
                
                // Extract content for display
                let displayContent = '';
                if (parsedContent.freeform_response && parsedContent.freeform_response.response) {
                  displayContent = parsedContent.freeform_response.response;
                } else if (parsedContent.blog_response) {
                  // For blog, we don't extract string - the structure is used directly
                  displayContent = ''; // Blog uses structured data, not string content
                } else if (parsedContent.email_response || parsedContent.email_sections) {
                  // For email, we don't extract string - the structure is used directly
                  displayContent = ''; // Email uses structured data, not string content
                  
                  // Process attachments and map them to email sections
                  if (post.attachments && Array.isArray(post.attachments) && post.attachments.length > 0) {
                    // Get image section names from metafields
                    let imageSectionNames = [];
                    if (savedVariables.image_section_names && Array.isArray(savedVariables.image_section_names)) {
                      imageSectionNames = savedVariables.image_section_names;
                    }
                    
                    // Create attachment map: imageSectionNames[index] -> attachment.url
                    const attachmentMap = {};
                    imageSectionNames.forEach((sectionName, index) => {
                      if (post.attachments[index] && post.attachments[index].url) {
                        attachmentMap[sectionName] = post.attachments[index].url;
                      }
                    });
                    
                    // Process email sections and apply attachments
                    let emailSectionsToProcess = [];
                    if (parsedContent.email_response && Array.isArray(parsedContent.email_response)) {
                      emailSectionsToProcess = parsedContent.email_response;
                    } else if (parsedContent.email_sections) {
                      emailSectionsToProcess = Array.isArray(parsedContent.email_sections) 
                        ? parsedContent.email_sections 
                        : Object.values(parsedContent.email_sections);
                    }
                    
                    if (emailSectionsToProcess.length > 0) {
                      const processedSections = emailSectionsToProcess.map((section, index) => {
                        const newSection = { ...section };
                        // Generate stable ID using the same format as when saving
                        // Try to match existing ID first, then generate using the same pattern
                        let sectionId = section.id;
                        if (!sectionId) {
                          // Use the same format as in emailSections sync: email-section-{index}-{component_name}
                          sectionId = `email-section-${index}-${section.component_name || 'text'}`;
                        }
                        // Also try alternative format for backward compatibility
                        const altSectionId = `section_${index}`;
                        
                        // Apply main image from attachment map - try both ID formats
                        if (attachmentMap[sectionId]) {
                          newSection.image = attachmentMap[sectionId];
                          // Also update image_placeholder if it exists
                          if (!newSection.image_placeholder) {
                            newSection.image_placeholder = attachmentMap[sectionId];
                          }
                        } else if (attachmentMap[altSectionId]) {
                          newSection.image = attachmentMap[altSectionId];
                          if (!newSection.image_placeholder) {
                            newSection.image_placeholder = attachmentMap[altSectionId];
                          }
                        }
                        
                        // Handle image1, image2, image3, image4 fields
                        ['image1', 'image2', 'image3', 'image4'].forEach(imgKey => {
                          const attachmentKey = `${sectionId}_${imgKey}`;
                          const altAttachmentKey = `${altSectionId}_${imgKey}`;
                          if (attachmentMap[attachmentKey]) {
                            newSection[imgKey] = attachmentMap[attachmentKey];
                          } else if (attachmentMap[altAttachmentKey]) {
                            newSection[imgKey] = attachmentMap[altAttachmentKey];
                          }
                        });
                        
                        // Handle features array images
                        if (section.features && Array.isArray(section.features)) {
                          newSection.features = section.features.map((feature, featureIndex) => {
                            const newFeature = { ...feature };
                            const featureImageKey = `${sectionId}_feature_${featureIndex}_image`;
                            const altFeatureImageKey = `${altSectionId}_feature_${featureIndex}_image`;
                            if (attachmentMap[featureImageKey]) {
                              newFeature.image = attachmentMap[featureImageKey];
                              newFeature.image_placeholder = attachmentMap[featureImageKey];
                            } else if (attachmentMap[altFeatureImageKey]) {
                              newFeature.image = attachmentMap[altFeatureImageKey];
                              newFeature.image_placeholder = attachmentMap[altFeatureImageKey];
                            }
                            return newFeature;
                          });
                        }
                        
                        // Ensure section has the stable ID
                        newSection.id = sectionId;
                        
                        return newSection;
                      });
                      
                      // Update parsed content with processed sections
                      if (parsedContent.email_response) {
                        parsedContent.email_response = processedSections;
                      } else if (parsedContent.email_sections) {
                        parsedContent.email_sections = processedSections;
                      }
                      
                      // Update generatedData with processed sections
                      setGeneratedData(parsedContent);
                      setEmailSections(processedSections);
                      setEmailData(processedSections);
                    }
                  }
                } else if (parsedContent.response) {
                  displayContent = parsedContent.response;
                } else if (parsedContent.content) {
                  displayContent = parsedContent.content;
                }
                
                // Get the JSON string for content type detection
                const jsonContentString = typeof post.generated_content === 'string' 
                  ? post.generated_content 
                  : JSON.stringify(post.generated_content);
                
                // Detect content type from the parsed JSON structure
                detectAndSetContentType(displayContent, jsonContentString);
                
                if (displayContent) {
                  setFinalContent(displayContent);
                  setParsedHTMLContent(displayContent);
                  setInitialContent(displayContent);
                }
                
                // Set flags to show content
                setHasFirstChunk(true);
                setIsStreamingDone(true);
                
                // Set saved state
                setSavedContentId(post.id);
                setIsContentSaved(true);
              } catch (e) {
                console.error('Error parsing generated content:', e);
                // If parsing fails, use as plain text
                setFinalContent(post.generated_content);
                setParsedHTMLContent(post.generated_content);
                setInitialContent(post.generated_content);
                setHasFirstChunk(true);
                setIsStreamingDone(true);
                setContentType('freeform');
              }
            }
          }
        } catch (err) {
          console.error('Error fetching content post:', err);
          toast.error("Failed to load saved content");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchContentPost();
  }, [location.state?.postId, location.state?.editMode]);

  // Fetch agent data if not provided
  useEffect(() => {
    const fetchAgentData = async () => {
      if (!agentData && agentName && !location.state?.editMode) {
        try {
          setLoading(true);
          // Fetch agent data from API
          const response = await axiosInstance.get('/api/v1/diy_agents', {
            params: {
              page: 1,
              size: 100 // Get all agents to find the one we need
            }
          });
          
          if (response.data && response.data.success) {
            const agents = response.data.data || [];
            // Find agent by name (convert slug back to name for matching)
            const agentSlug = agentName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            const agent = agents.find(a => {
              const aSlug = a.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
              return aSlug === agentSlug;
            });
            
            if (agent) {
              setAgentData(agent);
              
              // Initialize form data dynamically based on agent variables
              if (agent.variables) {
                try {
                  const variables = typeof agent.variables === 'string' 
                    ? JSON.parse(agent.variables) 
                    : agent.variables;
                  const fields = Array.isArray(variables) ? variables : (variables.fields || [variables]);
                  
                  const dynamicData = {};
                  fields.forEach(field => {
                    const fieldName = field.name || field.id || field.key;
                    if (fieldName) {
                      // Initialize with value from location.state if available, otherwise empty
                      const stateValue = location.state?.[fieldName];
                      if (stateValue !== undefined) {
                        dynamicData[fieldName] = stateValue;
                      } else {
                        // Set default based on field type
                        if (field.type === 'select' || field.type === 'language' || field.type === 'location' || field.type === 'location_focus') {
                          dynamicData[fieldName] = null;
                        } else {
                          dynamicData[fieldName] = '';
                        }
                      }
                    }
                  });
                  
                  // Merge with existing form data, preserving any existing values
                  setFormData(prev => ({ ...dynamicData, ...prev }));
                } catch (e) {
                  console.error('Error parsing variables:', e);
                }
              }
            } else {
              setError("Agent not found");
            }
          }
        } catch (err) {
          console.error('Error fetching agent data:', err);
          setError("Failed to load agent data");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAgentData();
  }, [agentName, agentData]);

  // Handle content generation
  const handleGenerate = async () => {
    if (!agentData) {
      toast.error("Agent data not available");
      return;
    }

    // Check if required fields are filled
    if (!formData.prompt || formData.prompt.trim() === '') {
      toast.error("Please enter a prompt");
      return;
    }

    setIsGenerating(true);
    setIsStreaming(true);
    setHasFirstChunk(false);
    setIsStreamingDone(false);
    setStreamingContent("");
    setParsedHTMLContent("");
    setFinalContent("");
    setGeneratedData(null);
    setFullJsonResponse(null); // Reset JSON response
    setContentType(null);
    // Reset saved state when generating new content
    setIsContentSaved(false);
    setSavedContentId(null);
    // Keep draftTitle but update it if formData.title changes

    try {
      // Generate unique session_id
      const timestamp = Date.now();
      const sessionId = `session_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
       
      // Get agent ID
      const fourayAgentId = agentData.id;

      // Extract query from prompt field (required static field)
      const query = formData.prompt || '';

      // Build variables object from formData - only include non-empty values
      // Exclude prompt and title as prompt goes to query field
      const variables = {};
      Object.keys(formData).forEach(key => {
        // Skip prompt (used for query) and title
        if (key === 'prompt' || key === 'title') {
          return;
        }
        
        const value = formData[key];
        // Skip null, undefined, empty string
        if (value !== null && value !== undefined && value !== '') {
          // Handle select objects (language, locationFocus, etc.)
          if (typeof value === 'object' && value.value !== undefined) {
            variables[key] = value.value;
          } else {
            variables[key] = value;
          }
        }
      });

      // Call the execute API with streaming
      await executeDiyAgent({
        sessionId,
        fourayAgentId,
        query,
        variables,
        streaming: true,
        onChunk: (chunk, accumulatedContent) => {
          // Check if this is the first chunk - for blog responses, accumulatedContent might be JSON string
          if (!hasFirstChunk) {
            // Set hasFirstChunk if we have any content (chunk or accumulatedContent)
            if (chunk || (accumulatedContent && accumulatedContent.trim())) {
              setHasFirstChunk(true);
            }
          }
          setStreamingContent(accumulatedContent);
          
          // Try to parse JSON during streaming to detect content type early
          // This is especially important for blog responses
          if (accumulatedContent && typeof accumulatedContent === 'string' && accumulatedContent.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(accumulatedContent);
              setGeneratedData(parsed);
              
              // Detect content type early for blog responses
              if (parsed.blog_response && parsed.blog_response.sections && Array.isArray(parsed.blog_response.sections)) {
                console.log('Detected blog_response structure during streaming');
                setContentType('blog');
                // Ensure hasFirstChunk is set for blog content
                if (!hasFirstChunk) {
                  setHasFirstChunk(true);
                }
              } else if (parsed.sections && Array.isArray(parsed.sections)) {
                console.log('Detected sections array during streaming');
                setContentType('blog');
                // Ensure hasFirstChunk is set for blog content
                if (!hasFirstChunk) {
                  setHasFirstChunk(true);
                }
              } else if (parsed.email_response || parsed.email_sections) {
                console.log('Detected email_response/email_sections during streaming');
                setContentType('email');
                // Set generatedData early for email content
                if (parsed.email_response) {
                  setGeneratedData({ email_response: parsed.email_response });
                } else if (parsed.email_sections) {
                  setGeneratedData({ email_sections: parsed.email_sections });
                }
                if (!hasFirstChunk) {
                  setHasFirstChunk(true);
                }
              } else if (parsed.freeform_response && parsed.freeform_response.response) {
                setContentType('freeform');
              }
            } catch (e) {
              // JSON is incomplete, continue streaming
            }
          }
        },
        onComplete: (finalContent, jsonContent) => {
          // After streaming is done, detect content type and parse
          if (finalContent || jsonContent) {
            let contentToUse = finalContent;
            
            // Store the full JSON response
            if (jsonContent) {
              setFullJsonResponse(jsonContent);
              try {
                const parsed = JSON.parse(jsonContent);
                setGeneratedData(parsed);
                
                // For freeform content, extract response.response if available for display
                if (parsed.freeform_response && parsed.freeform_response.response) {
                  contentToUse = parsed.freeform_response.response;
                } else if (parsed.blog_response) {
                  // For blog, we don't extract string - the structure is used directly
                  // Content type will be detected from the structure
                  contentToUse = ''; // Blog uses structured data, not string content
                } else if (parsed.email_response || parsed.email_sections) {
                  // For email, we don't extract string - the structure is used directly
                  // Content type will be detected from the structure
                  contentToUse = ''; // Email uses structured data, not string content
                } else if (parsed.response && typeof parsed.response === 'string') {
                  contentToUse = parsed.response;
                }
                
                // Detect content type from parsed JSON (do this after setting generatedData)
                detectAndSetContentType(contentToUse || finalContent, jsonContent);
                
                // Ensure hasFirstChunk is set if we have blog/email content
                if (parsed.blog_response || parsed.email_response || parsed.email_sections || (parsed.sections && Array.isArray(parsed.sections))) {
                  setHasFirstChunk(true);
                }
              } catch (e) {
                console.error('Error parsing JSON in onComplete:', e);
                // If not JSON, detect from content string
                detectAndSetContentType(finalContent, null);
              }
            } else {
              // No JSON content, detect from string content
              detectAndSetContentType(finalContent, null);
            }
            
            // Set content for display
            const displayContent = contentToUse || finalContent || '';
            setFinalContent(displayContent);
            setParsedHTMLContent(displayContent);
          }
          
          // Ensure hasFirstChunk is set when streaming is done (important for blog/email)
          if (!hasFirstChunk && (finalContent || jsonContent)) {
            setHasFirstChunk(true);
          }
          
          setIsStreamingDone(true);
          setIsStreaming(false);
          toast.success("Content generated successfully!");
        },
        onError: (error) => {
          console.error('Error generating content:', error);
          toast.error(`Failed to generate content: ${error.message}`);
          setIsStreaming(false);
        }
      });
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(`Failed to generate content: ${error.message}`);
      setIsStreaming(false);
    } finally {
      setIsGenerating(false);
    }
  };

  // Detect content type from API response
  const detectAndSetContentType = (content, jsonData) => {
    try {
      // Try to parse JSON data if available
      if (jsonData) {
        const parsed = JSON.parse(jsonData);
        
        // Check for blog structure (sections array)
        // Check both root level and blog_response structure
        if ((parsed.blog_response && parsed.blog_response.sections && Array.isArray(parsed.blog_response.sections)) ||
            (parsed.sections && Array.isArray(parsed.sections))) {
          console.log('Detected blog content type:', parsed.blog_response ? 'blog_response' : 'sections');
          setContentType('blog');
          return;
        }
        
        // Check for email structure (email_response, email_sections, or sections object/array)
        if (parsed.email_response || parsed.email_sections || (parsed.sections && typeof parsed.sections === 'object')) {
          console.log('Detected email content type:', parsed.email_response ? 'email_response' : parsed.email_sections ? 'email_sections' : 'sections');
          setContentType('email');
          return;
        }
      }

      // Check content string for patterns
      const contentLower = content.toLowerCase();
      
      // Check for blog-like structure (H1, H2 headings, sections)
      if (content.includes('h1_title') || content.includes('sections') || 
          content.match(/section_title|section_content/i)) {
        setContentType('blog');
        return;
      }
      
      // Check for email-like structure
      if (content.includes('banner-section') || content.includes('email_section') ||
          content.includes('component_name')) {
        setContentType('email');
        return;
      }

      // Default to freeform
      setContentType('freeform');
    } catch (e) {
      console.error('Error detecting content type:', e);
      setContentType('freeform');
    }
  };

  // Handle image selection
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setImageError("Image size should be less than 10MB");
      return;
    }

    setSelectedImage(file);
    setImageError("");

    // Upload the image immediately
    await uploadImage(file);
  };

  const uploadImage = async (file) => {
    if (!file) return;

    setIsUploadingImage(true);
    setImageError("");

    try {
      const formData = new FormData();
      formData.append("file[]", file);
      
      // Get the current company ID dynamically
      const currentCompanyId = await getCurrentCompanyId();
      if (!currentCompanyId) {
        setImageError("Could not determine company ID. Please try again.");
        return;
      }
      
      const jsonPayload = {
        request_type: "blog",
        additional_data: {
          documents: [currentCompanyId],
          uid: Date.now().toString() + "-" + Math.random().toString(36).substring(2, 6),
        },
      };
      formData.append("request_json", JSON.stringify(jsonPayload));

      // Upload the file
      const response = await uploadCompanyKnowledgeFiles(formData);
      
      if (response.files && response.files.length > 0) {
        // Wait a moment for the backend to process
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Fetch the permanent URL from the company's knowledge base
        const permanentUrl = await getPermanentImageUrlFromKnowledgeBase(currentCompanyId, file.name);
        
        if (permanentUrl) {
          setImageUrl(permanentUrl);
        } else {
          setImageUrl(response.files[0]);
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setImageError(`Upload failed: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const getCurrentCompanyId = async () => {
    // Use companyId from context instead of making API call
    return companyId || null;
  };

  const getPermanentImageUrlFromKnowledgeBase = async (companyId, fileName) => {
    try {
      // Use companyKnowledgeDocs from context instead of making API call
      if (companyKnowledgeDocs && Array.isArray(companyKnowledgeDocs)) {
        const sortedDocs = companyKnowledgeDocs
          .filter(doc => {
            const contentType = doc.content_type || (doc.url && doc.url.match(/\.(jpg|jpeg|png|gif|webp)/i) ? 'image/jpeg' : null);
            return contentType && contentType.startsWith('image/');
          })
          .sort((a, b) => {
            const dateA = a.uploaded_at ? new Date(a.uploaded_at) : new Date(0);
            const dateB = b.uploaded_at ? new Date(b.uploaded_at) : new Date(0);
            return dateB - dateA;
          });
        
        if (sortedDocs.length > 0) {
          return sortedDocs[0].url || sortedDocs[0];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get permanent image URL from knowledge base:', error);
      return null;
    }
  };

  const handleReplaceImage = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setImageUrl("");
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBack = () => {
    navigate('/skills');
  };

  // Helper function to extract HTML from content
  const extractHtmlFromContent = (content) => {
    if (!content || typeof content !== 'string') return '';
    
    // If content is wrapped in HTML tags, extract the inner content
    const htmlMatch = content.match(/<html[^>]*>([\s\S]*)<\/html>/i);
    if (htmlMatch) {
      return htmlMatch[1].trim();
    }
    
    // If content is wrapped in body tags, extract the inner content
    const bodyMatch = content.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }
    
    return content.trim();
  };

  // Helper function to convert HTML to plain text (for editing)
  const htmlToPlainText = (html) => {
    if (!html || typeof html !== 'string') return '';
    
    // If it's complex HTML, return as-is
    if (/<[^>]+>/.test(html) && !/^<br\s*\/?>$/i.test(html.trim())) {
      // Complex HTML - return as-is
      return html;
    }
    
    // Only contains <br> tags, safe to convert
    return html.replace(/<br\s*\/?>/gi, '\n');
  };

  // Handle edit mode toggle
  const handleToggleEditMode = () => {
    if (!isEditMode) {
      let currentContent = "";
      if (initialContent && initialContent.trim()) {
        currentContent = initialContent;
      } else if (finalContent) {
        currentContent = finalContent;
      } else if (parsedHTMLContent) {
        currentContent = parsedHTMLContent;
      } else if (streamingContent) {
        currentContent = streamingContent;
      }
      
      // If we have generatedData with response.response, extract it
      if (!currentContent && generatedData && generatedData.response) {
        currentContent = generatedData.response;
      }
      
      const cleanedContent = extractHtmlFromContent(currentContent);
      // Convert HTML back to plain text for editing
      const plainTextContent = htmlToPlainText(cleanedContent);
      setEditedContent(plainTextContent || "");
    }
    setIsEditMode(!isEditMode);
  };

  // Helper function to preserve line breaks in plain text only
  const preserveWhitespace = (content) => {
    if (!content || typeof content !== 'string') return content;
    
    // If content already contains HTML tags, return as-is (don't modify HTML)
    if (/<[^>]+>/.test(content)) {
      return content;
    }
    
    // Only for plain text: convert line breaks to <br> tags
    return content.replace(/\n/g, '<br>');
  };

  // Handle saving edited content
  const handleSaveEditedContent = () => {
    const cleanedContent = extractHtmlFromContent(editedContent);
    const preservedContent = preserveWhitespace(cleanedContent);
    
    // Update all content states to ensure the renderer shows the updated content
    setInitialContent(preservedContent);
    setParsedHTMLContent(preservedContent);
    setFinalContent(preservedContent);
    setStreamingContent(preservedContent);
    
    // Set hasFirstChunk to true so content displays in normal mode
    setHasFirstChunk(true);
    setIsStreamingDone(true);
    
    // Update fullJsonResponse to include the edited content
    if (fullJsonResponse) {
      try {
        const parsed = typeof fullJsonResponse === 'string' 
          ? JSON.parse(fullJsonResponse) 
          : fullJsonResponse;
        
        // Update freeform_response.response with edited content
        if (parsed.freeform_response) {
          parsed.freeform_response.response = preservedContent;
        } else {
          // If no freeform_response structure, create it
          parsed.freeform_response = { response: preservedContent };
        }
        
        // Update the fullJsonResponse string
        setFullJsonResponse(JSON.stringify(parsed));
        
        // Also update generatedData to reflect the change
        setGeneratedData(parsed);
      } catch (e) {
        console.error('Error updating JSON with edited content:', e);
      }
    } else if (generatedData) {
      // If no fullJsonResponse but we have generatedData, update it
      const updatedData = { ...generatedData };
      if (updatedData.freeform_response) {
        updatedData.freeform_response.response = preservedContent;
      } else {
        updatedData.freeform_response = { response: preservedContent };
      }
      setGeneratedData(updatedData);
      setFullJsonResponse(JSON.stringify(updatedData));
    } else {
      // If no JSON structure exists, create a new one
      const newJson = {
        freeform_response: {
          response: preservedContent
        }
      };
      setGeneratedData(newJson);
      setFullJsonResponse(JSON.stringify(newJson));
    }
    
    // Set content type to freeform if not already set
    if (!contentType) {
      setContentType('freeform');
    }
    
    if (cleanedContent.trim()) {
      toast.success("Content saved successfully!");
    } else {
      toast.info("Editor cleared. You can add content manually.");
    }
    setIsEditMode(false);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    let currentContent = "";
    if (initialContent && initialContent.trim()) {
      currentContent = initialContent;
    } else if (finalContent) {
      currentContent = finalContent;
    } else if (parsedHTMLContent) {
      currentContent = parsedHTMLContent;
    } else if (streamingContent) {
      currentContent = streamingContent;
    }
    setEditedContent(currentContent || "");
    setIsEditMode(false);
  };

  // Get final content for saving - return full JSON response if available
  const getFinalContentForSave = () => {
    // If we have edited content (initialContent), update the JSON structure with it
    if (initialContent && initialContent.trim()) {
      if (fullJsonResponse) {
        try {
          const parsed = typeof fullJsonResponse === 'string' 
            ? JSON.parse(fullJsonResponse) 
            : fullJsonResponse;
          
          // Update freeform_response.response with edited content
          if (parsed.freeform_response) {
            parsed.freeform_response.response = initialContent;
          } else {
            parsed.freeform_response = { response: initialContent };
          }
          
          return JSON.stringify(parsed);
        } catch (e) {
          console.error('Error updating JSON with edited content:', e);
          // Fall back to creating new JSON structure
          return JSON.stringify({ freeform_response: { response: initialContent } });
        }
      } else if (generatedData) {
        // If no fullJsonResponse but we have generatedData, update it
        const updatedData = { ...generatedData };
        if (updatedData.freeform_response) {
          updatedData.freeform_response.response = initialContent;
        } else {
          updatedData.freeform_response = { response: initialContent };
        }
        return JSON.stringify(updatedData);
      } else {
        // If no JSON structure exists, create a new one
        return JSON.stringify({ freeform_response: { response: initialContent } });
      }
    }
    
    // If we have the full JSON response from API, return that
    if (fullJsonResponse) {
      return fullJsonResponse;
    }
    
    // Otherwise, return the content (for manually edited content)
    if (finalContent) {
      return finalContent;
    }
    if (parsedHTMLContent) {
      return parsedHTMLContent;
    }
    if (streamingContent) {
      return streamingContent;
    }
    return "";
  };

  // Handle save draft
  const handleSaveDraft = () => {
    const content = getFinalContentForSave();
    if (!content || !content.trim()) {
      toast.error("No content to save. Please generate content first.");
      return;
    }

    // If content is already saved, show modal to update or create new
    if (isContentSaved && savedContentId) {
      setShowDraftModal(true);
    } else {
      // New content - show name field inline or modal
      setShowDraftModal(true);
    }
  };

  // Handle draft modal confirmation
  const handleDraftModalConfirm = async (choice, newDraftTitle) => {
    setIsSavingDraft(true);
    setShowDraftModal(false);

    try {
      const content = getFinalContentForSave();
      if (!content || !content.trim()) {
        toast.error("No content to save.");
        return;
      }

      // Build variables object from formData (only exclude title and prompt as they're used for content post)
      const variables = {};
      Object.keys(formData).forEach(key => {
        // Only exclude title and prompt - all other fields go to metafields
        if (key === 'prompt' || key === 'title') {
          return;
        }
        const value = formData[key];
        if (value !== null && value !== undefined && value !== '') {
          if (typeof value === 'object' && value.value !== undefined) {
            variables[key] = value.value;
          } else {
            variables[key] = value;
          }
        }
      });

      // Generate session_id if not available
      const sessionId = location.state?.sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Use the shared draftTitle, or new title from modal, or formData title, or default
      const finalTitle = choice === "new" 
        ? (newDraftTitle || draftTitle || formData.title || `${agentData?.name || 'Content'} Draft`)
        : draftTitle;

      // Convert variables to JSON string for metafields
      const metafieldsJson = Object.keys(variables).length > 0 ? JSON.stringify(variables) : '{}';

      // Prepare content data with images if it's email content
      const contentData = {
        title: finalTitle,
        description: finalTitle, // Send title in description as well (for now)
        post_type: agentData?.name || 'diy_agent',
        session_id: sessionId,
        agent_id: agentData?.id?.toString() || '',
        generated_content: content,
        metafields: metafieldsJson, // Send variables as JSON string in metafields
      };

      // If email content, extract sections and prepare images
      let emailSectionsForSave = [];
      if (contentType === 'email') {
        // Priority: emailSectionsRef (from context via ref - most up-to-date) > emailSections (state) > emailData (array) > generatedData
        if (emailSectionsRef.current && emailSectionsRef.current.length > 0) {
          emailSectionsForSave = emailSectionsRef.current;
        } else if (emailSections && emailSections.length > 0) {
          emailSectionsForSave = emailSections;
        } else if (Array.isArray(emailData) && emailData.length > 0) {
          emailSectionsForSave = emailData;
        } else {
          // Try to extract from generatedData
          if (generatedData?.email_response && Array.isArray(generatedData.email_response)) {
            emailSectionsForSave = generatedData.email_response;
          } else if (generatedData?.sections && Array.isArray(generatedData.sections)) {
            emailSectionsForSave = generatedData.sections;
          } else if (generatedData?.email_sections) {
            emailSectionsForSave = Array.isArray(generatedData.email_sections) 
              ? generatedData.email_sections 
              : Object.values(generatedData.email_sections);
          }
        }
      }

      let response;
      if (choice === "update" && savedContentId) {
        // Update existing draft
        response = await updateContentPostDraft(savedContentId, contentData, emailSectionsForSave);
      } else {
        // Create new draft
        response = await saveContentPostDraft(contentData, emailSectionsForSave);
      }

      if (response && (response.data || response.content_post)) {
        const savedPost = response.data || response.content_post;
        setSavedContentId(savedPost.id || savedPost._id);
        setIsContentSaved(true);
        setDraftTitle(finalTitle); // Update shared title variable
        toast.success(choice === "update" ? "Draft updated successfully!" : "Draft saved successfully!");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(`Failed to save draft: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSavingDraft(false);
    }
  };


  // Render content based on detected type
  const renderContent = () => {
    if (!contentType) {
      return null;
    }

    if (contentType === 'blog') {
      // Render blog sections
      return renderBlogContent();
    } else if (contentType === 'email') {
      // Render email sections inline in the playground
      return renderEmailContent();
    } else {
      // Render freeform content
      return (
        <StreamingContentRenderer
          title={formData.title || agentData?.name || "Generated Content"}
          isStreaming={isStreaming}
          isGenerating={isGenerating}
          isTranslating={false}
          hasFirstChunk={hasFirstChunk}
          isStreamingDone={isStreamingDone}
          streamingContent={streamingContent}
          parsedHTMLContent={parsedHTMLContent}
          finalContent={finalContent}
          initialContent={initialContent}
          noContentMessage="No content generated yet. Fill in the form and click Generate to see results."
        />
      );
    }
  };

  // Helper function to render section content (similar to BlogPlayground)
  const renderSectionContent = (content, sectionTitle) => {
    if (!content) return "No content available.";

    if (content === null || content === undefined) {
      return "No content available.";
    }

    // Handle FAQ sections
    if (sectionTitle && sectionTitle.toLowerCase().includes('frequently asked questions')) {
      if (Array.isArray(content)) {
        return (
          <div>
            {content.map((faq, index) => (
              <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ 
                    backgroundColor: '#E36250', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '28px', 
                    height: '28px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '14px', 
                    marginRight: '12px',
                    flexShrink: 0
                  }}>
                    Q
                  </span>
                  {faq.question}
                </div>
                <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        );
      } else if (typeof content === 'string' && content.includes('Q:') && content.includes('A:')) {
        const faqBlocks = content.split(/\n\s*\n/).filter(block => block.trim());
        return (
          <div>
            {faqBlocks.map((block, index) => {
              const lines = block.split('\n').filter(line => line.trim());
              const questionLine = lines.find(line => line.startsWith('Q:') || line.startsWith('Q:'));
              const answerLine = lines.find(line => line.startsWith('A:') || line.startsWith('A:'));
              
              if (questionLine && answerLine) {
                const question = questionLine.replace(/^Q:\s*/, '').trim();
                const answer = answerLine.replace(/^A:\s*/, '').trim();
                
                return (
                  <div key={index} style={{ marginBottom: 20, padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#E36250', marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ 
                        backgroundColor: '#E36250', 
                        color: 'white', 
                        borderRadius: '50%', 
                        width: '28px', 
                        height: '28px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '14px', 
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        Q
                      </span>
                      {question}
                    </div>
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, paddingLeft: '40px' }}>
                      {answer}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      }
    }

    // Handle array content
    if (Array.isArray(content)) {
      return (
        <div>
          {content.map((item, index) => (
            <div key={index} style={{ marginBottom: '12px', lineHeight: 1.6 }}>
              {typeof item === 'string' ? (
                <div dangerouslySetInnerHTML={{ __html: item.replace(/\n/g, '<br/>') }} />
              ) : (
                <div>{JSON.stringify(item)}</div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle string content with markdown-like formatting
    if (typeof content === 'string') {
      // Convert markdown-style formatting to HTML
      let htmlContent = content
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>');
      
      return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }

    return <div>{String(content)}</div>;
  };

  // Handle section deletion
  const handleDeleteSection = (idx) => {
    const updatedSections = [...blogSections];
    updatedSections.splice(idx, 1);
    setBlogSections(updatedSections);
    
    // Update generatedData
    if (generatedData?.blog_response) {
      setGeneratedData({
        ...generatedData,
        blog_response: {
          ...generatedData.blog_response,
          sections: updatedSections
        }
      });
    } else if (generatedData) {
      setGeneratedData({
        ...generatedData,
        sections: updatedSections
      });
    }
    
    setUpdateTrigger(prev => prev + 1);
    toast.success("Section deleted");
  };

  // Sync blogSections with generatedData when it changes
  useEffect(() => {
    if (generatedData) {
      let sections = [];
      if (generatedData?.blog_response?.sections && Array.isArray(generatedData.blog_response.sections)) {
        sections = generatedData.blog_response.sections;
      } else if (generatedData?.sections && Array.isArray(generatedData.sections)) {
        sections = generatedData.sections;
      }
      
      if (sections.length > 0) {
        setBlogSections(prevSections => {
          // Only update if sections have actually changed
          const prevStr = JSON.stringify(prevSections);
          const newStr = JSON.stringify(sections);
          if (prevStr !== newStr) {
            return sections;
          }
          return prevSections;
        });
      }
    }
  }, [generatedData]);

  // Sync email sections with generatedData when it changes
  useEffect(() => {
    if (generatedData && contentType === 'email') {
      let sections = [];
      
      // Check for email_response first (array format)
      if (generatedData?.email_response && Array.isArray(generatedData.email_response)) {
        sections = generatedData.email_response;
      } else if (generatedData?.sections && Array.isArray(generatedData.sections)) {
        sections = generatedData.sections;
      } else if (generatedData?.email_sections) {
        sections = Array.isArray(generatedData.email_sections) 
          ? generatedData.email_sections 
          : Object.values(generatedData.email_sections);
      }
      
      // Ensure all sections have IDs and component_name (use stable IDs based on index)
      const sectionsWithIds = sections.map((section, idx) => {
        // Use existing ID if available, otherwise create stable ID based on index and component_name
        const stableId = section.id || `email-section-${idx}-${section.component_name || 'text'}`;
        return {
          ...section,
          id: stableId,
          component_name: section.component_name || "Text"
        };
      });
      
      if (sectionsWithIds.length > 0) {
        setEmailSections(prevSections => {
          // Deep comparison to avoid unnecessary updates
          const prevStr = JSON.stringify(prevSections);
          const newStr = JSON.stringify(sectionsWithIds);
          if (prevStr !== newStr) {
            return sectionsWithIds;
          }
          return prevSections;
        });
        // Only update emailData if it's actually different
        setEmailData(prev => {
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(sectionsWithIds);
          if (prevStr !== newStr) {
            return sectionsWithIds;
          }
          return prev;
        });
      }
    }
  }, [generatedData, contentType]);

  // Render blog content with sections
  const renderBlogContent = () => {
    let sections = blogSections.length > 0 ? blogSections : [];
    
    // If blogSections is empty, try to extract from generatedData
    if (sections.length === 0) {
      if (generatedData?.blog_response?.sections && Array.isArray(generatedData.blog_response.sections)) {
        sections = generatedData.blog_response.sections;
      } else if (generatedData?.sections && Array.isArray(generatedData.sections)) {
        sections = generatedData.sections;
      } else if (generatedData?.final_blog_data?.sections) {
        sections = generatedData.final_blog_data.sections;
      } else {
        // Try to parse from content string
        try {
          const parsed = JSON.parse(finalContent || streamingContent);
          if (parsed.blog_response && parsed.blog_response.sections) {
            sections = parsed.blog_response.sections;
          } else if (parsed.sections) {
            sections = parsed.sections;
          }
        } catch (e) {
          // If not JSON, treat as plain text
          sections = [{
            section_title: "Content",
            content: finalContent || streamingContent
          }];
        }
      }
    }
    
    // Get h1_title from blog_response if available
    const h1Title = generatedData?.blog_response?.h1_title || formData.title || agentData?.name || "Generated Blog";

    return (
      <Card className="create-form-panel">
        <Card.Body>
          <h5 className="mb-4">{h1Title}</h5>
          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {sections && sections.map((section, idx) => (
              <div 
                key={`section-${idx}-${updateTrigger}-${section.lastUpdated ?? 0}-${
                  (Array.isArray(section.content)
                    ? section.content.join(' ')
                    : (section.content ?? '')
                  ).slice(0, 20) || 'empty'
                }`}
                className="outline-section-item"
                style={{padding: '20px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}
              >
                <div className="section-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                  <div className="outline-title" style={{flex: 1, minWidth: 0, marginRight: '16px'}}>
                    <h4 style={{
                      margin: 0, 
                      color: '#1f2937', 
                      fontSize: '14px', 
                      fontWeight: 600,
                      wordWrap: 'break-word',
                      lineHeight: '1.3'
                    }}>
                      {section.section_title || section.title || `Section ${idx + 1}`}
                    </h4>
                  </div>
                  
                  <div className="button-group" style={{display: 'flex', gap: '8px', flexWrap: 'nowrap', flexShrink: 0}}>
                    <button 
                      className="btn btn-outline-secondary" 
                      style={{borderRadius: '6px', padding: '6px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}
                      onClick={() => {
                        toast.info("Regenerate functionality coming soon");
                      }}
                    >
                      <BiRedo size={16} /> Regenerate text
                    </button>
                    <button 
                      className="btn btn-outline-secondary" 
                      style={{borderRadius: '6px', padding: '6px 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px'}}
                      onClick={() => {
                        setEditingSectionIdx(idx);
                        // Convert content to editable text format
                        let editText = '';
                        if (Array.isArray(section.content)) {
                          if (section.section_title && section.section_title.toLowerCase().includes('frequently asked questions')) {
                            editText = section.content.map(faq => `Q: ${faq.question}\nA: ${faq.answer}`).join('\n\n');
                          } else {
                            editText = section.content.join('\n');
                          }
                        } else {
                          editText = String(section.content || '');
                        }
                        setEditSectionText(editText);
                      }}
                    >
                      <BiEdit size={16} /> Edit text
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      style={{borderRadius: '6px', padding: '6px 12px', fontSize: '14px'}}
                      onClick={() => handleDeleteSection(idx)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {editingSectionIdx === idx ? (
                  <div style={{marginBottom: 8}}>
                    <textarea
                      value={editSectionText}
                      onChange={e => setEditSectionText(e.target.value)}
                      className="form-control"
                      rows={4}
                      style={{borderRadius: '8px', border: '1px solid #d1d5db', padding: '12px'}}
                    />
                    <div style={{marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                      <button className="btn bgprimary" style={{borderRadius: '6px', padding: '8px 16px'}} onClick={() => {
                        const updated = [...sections];
                        // Parse edited text back to appropriate format
                        let parsedContent = editSectionText;
                        
                        if (section.section_title && section.section_title.toLowerCase().includes('frequently asked questions')) {
                          // Parse FAQ format back to FAQ objects
                          if (editSectionText.includes('Q:') && editSectionText.includes('A:')) {
                            const qaPairs = editSectionText.split(/\n\s*\n/).filter(pair => pair.trim());
                            parsedContent = qaPairs.map(pair => {
                              const lines = pair.split('\n').filter(line => line.trim());
                              const question = lines.find(line => line.startsWith('Q:'))?.replace('Q:', '').trim();
                              const answer = lines.find(line => line.startsWith('A:'))?.replace('A:', '').trim();
                              return { question, answer };
                            }).filter(qa => qa.question && qa.answer);
                          } else {
                            parsedContent = editSectionText;
                          }
                        } else if (Array.isArray(section.content)) {
                          parsedContent = editSectionText.split('\n').filter(line => line.trim());
                        }
                        
                        updated[idx].content = parsedContent;
                        setBlogSections(updated);
                        
                        // Update generatedData
                        if (generatedData?.blog_response) {
                          setGeneratedData({
                            ...generatedData,
                            blog_response: {
                              ...generatedData.blog_response,
                              sections: updated
                            }
                          });
                        } else if (generatedData) {
                          setGeneratedData({
                            ...generatedData,
                            sections: updated
                          });
                        }
                        
                        setEditingSectionIdx(null);
                        setEditSectionText("");
                        setUpdateTrigger(prev => prev + 1);
                        toast.success("Section updated");
                      }}>Save</button>
                      <button className="btn btn-outline-secondary" style={{borderRadius: '6px', padding: '8px 16px'}} onClick={() => {
                        setEditingSectionIdx(null);
                        setEditSectionText("");
                      }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="section-content" style={{padding: '16px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #f3f4f6'}}>
                    {renderSectionContent(section.content, section.section_title)}
                  </div>
                )}
                {section.suggested_image_or_infographic && (
                  <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a', color: '#92400e', fontStyle: 'italic', fontSize: '14px' }}>
                    💡 <strong>Suggested Visual:</strong> {section.suggested_image_or_infographic}
                  </div>
                )}
                {section.keywords_integrated && Array.isArray(section.keywords_integrated) && section.keywords_integrated.length > 0 && (
                  <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#dbeafe', borderRadius: '6px', border: '1px solid #93c5fd' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
                      Related Keywords:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {section.keywords_integrated.map((keyword, keywordIdx) => (
                        <span key={keywordIdx} style={{ 
                          backgroundColor: '#3b82f6', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px' 
                        }}>
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {section.word_count && (
                  <div style={{ marginTop: 12, textAlign: 'right', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                    {section.word_count} words
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Sync email sections from context to ref for saving (using ref to avoid infinite loops)
  const EmailSectionsSync = () => {
    const { sections: sectionsFromContext } = useEmailSections();
    const prevSectionsStrRef = useRef(null);
    
    // Update ref without causing re-renders - only when sections actually change
    useEffect(() => {
      if (sectionsFromContext && sectionsFromContext.length > 0) {
        const newSectionsStr = JSON.stringify(sectionsFromContext);
        if (prevSectionsStrRef.current !== newSectionsStr) {
          prevSectionsStrRef.current = newSectionsStr;
          emailSectionsRef.current = sectionsFromContext;
        }
      }
    }, [sectionsFromContext]);
    
    return null;
  };

  // Email content wrapper component (similar to EditEmail) - renders inline in playground
  const EmailContentWrapper = React.memo(() => {
    const { sections: sectionsFromContext } = useEmailSections();

    const handleHeroImageChange = useCallback((img) => {
      setEmailData(prev => {
        if (Array.isArray(prev)) {
          return prev;
        }
        return {
          ...prev,
          heroImage: img
        };
      });
    }, []);

    const handleUpdateEmailData = useCallback((updatedData) => {
      if (Array.isArray(updatedData)) {
        setEmailData(updatedData);
      } else {
        setEmailData(prev => ({ ...prev, ...updatedData }));
      }
    }, []);

    return (
      <div className="col position-relative" style={{ minHeight: "500px", backgroundColor: 'rgba(208, 71, 52, 0.04)' }}>
        <EmailHeaderImage
          initialHeroImage={emailData?.heroImage || emailData?.[0]?.heroImg || emailData?.heroImg}
          onHeroImageChange={handleHeroImageChange}
        />
        <EmailDetailsContainer
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          backgroundColor={backgroundColor}
          fontFamily={fontFamily}
          onUpdateEmailData={handleUpdateEmailData}
        />
      </div>
    );
  });

  // Memoize email sections extraction to prevent infinite loops
  const emailSectionsForRender = useMemo(() => {
    let sections = emailSections.length > 0 ? emailSections : [];
    
    // If emailSections is empty, try to extract from generatedData
    if (sections.length === 0) {
      // Check for email_response first (array format)
      if (generatedData?.email_response && Array.isArray(generatedData.email_response)) {
        sections = generatedData.email_response;
      } else if (generatedData?.sections && Array.isArray(generatedData.sections)) {
        sections = generatedData.sections;
      } else if (generatedData?.email_sections) {
        sections = Array.isArray(generatedData.email_sections) 
          ? generatedData.email_sections 
          : Object.values(generatedData.email_sections);
      } else {
        // Try to parse from content string
        try {
          const parsed = JSON.parse(finalContent || streamingContent);
          if (parsed.email_response && Array.isArray(parsed.email_response)) {
            sections = parsed.email_response;
          } else if (parsed.sections) {
            sections = Array.isArray(parsed.sections) 
              ? parsed.sections 
              : Object.values(parsed.sections);
          } else if (parsed.email_sections) {
            sections = Array.isArray(parsed.email_sections) 
              ? parsed.email_sections 
              : Object.values(parsed.email_sections);
          }
        } catch (e) {
          // If not JSON, treat as plain text
          sections = [{
            id: `email-section-0-text`,
            component_name: "Text",
            content: finalContent || streamingContent
          }];
        }
      }
    }

    // Ensure all sections have IDs and component_name (use stable IDs)
    return sections.map((section, idx) => {
      // Use existing ID if available, otherwise create stable ID based on index and component_name
      const stableId = section.id || `email-section-${idx}-${section.component_name || 'text'}`;
      return {
        ...section,
        id: stableId,
        component_name: section.component_name || "Text"
      };
    });
  }, [emailSections, generatedData, finalContent, streamingContent]);

  // Memoize stable key to prevent provider remounting
  const emailProviderKey = useMemo(() => {
    return emailSectionsForRender.map(s => s.id).join('-');
  }, [emailSectionsForRender]);

  // Render email content with sections
  const renderEmailContent = () => {
    return (
      <EmailSectionProvider key={emailProviderKey} initialSections={emailSectionsForRender}>
        <EmailSectionsSync />
        <EmailContentWrapper />
      </EmailSectionProvider>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Loader />
        <div style={{ marginLeft: '10px' }}>Loading agent playground...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <ToastContainer />
        <Breadcrumb />
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error}</p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/skills')}
          >
            Back to Skills
          </Button>
        </Alert>
      </div>
    );
  }

  if (!agentData) {
    return (
      <div style={{ padding: '20px' }}>
        <ToastContainer />
        <Breadcrumb />
        <Alert variant="warning">
          <h4>Agent Not Found</h4>
          <p>Unable to load agent data. Please try again.</p>
          <Button 
            variant="primary" 
            onClick={() => navigate('/skills')}
          >
            Back to Skills
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <ToastContainer />
      <Breadcrumb />
      
      <div className="mb-4">
        <h3>{agentData.name}</h3>
        {agentData.description && (
          <p className="text-muted">{agentData.description}</p>
        )}
      </div>

      <Row>
        {/* Left: Input Form */}
        <Col md={4}>
          <Card className="create-form-panel">
            <Card.Body>
              <Form onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}>
                {/* Dynamic Form based on agent variables */}
                {agentData?.variables ? (
                  <DynamicAgentForm
                    variables={agentData.variables}
                    formData={formData}
                    setFormData={setFormData}
                    allLocations={allLocations}
                    imageUrl={imageUrl}
                    imageError={imageError}
                    isUploadingImage={isUploadingImage}
                    fileInputRef={fileInputRef}
                    handleImageSelect={handleImageSelect}
                    handleReplaceImage={handleReplaceImage}
                    removeImage={removeImage}
                  />
                ) : (
                  // Fallback to default form if no variables defined
                  <div className="mb-4">
                    <h6 className="section-title mb-3">Provide Context</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter a title for your content..."
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={6}
                        placeholder="Enter your prompt or question here..."
                        value={formData.prompt}
                        onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                        required
                      />
                    </Form.Group>
                  </div>
                )}

                <div className="form-actions">
                
                  <Button 
                    type="submit" 
                    className="generate-btn"
                    variant="danger"
                    disabled={isGenerating || isStreaming}
                  >
                    {isGenerating || isStreaming ? "Generating..." : "Generate"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Streaming and Rendered Output */}
        <Col md={8} className="border-start">
          <div className="d-flex gap-4 align-items-center mb-3">
            <h5 style={{ fontWeight: "500", fontSize: "16px" }}>
              Generated Content
            </h5>
            
            {/* Edit button - always show for freeform content to allow manual entry */}
           
            {/* Preview buttons - show after content is generated */}
            {(hasFirstChunk || initialContent) && (
              <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
                <h6 className="mt-2 mx-1">Preview</h6>
                <div className="d-flex justify-content-center align-items-center">
                  <button 
                    className="btn" 
                    onClick={() => {
                      const content = getFinalContentForSave();
                      
                      // Determine content type
                      let detectedContentType = contentType;
                      if (!detectedContentType && generatedData) {
                        if (generatedData.blog_response || generatedData.blog_response?.sections) {
                          detectedContentType = 'blog';
                        } else if (generatedData.email_response || generatedData.email_sections) {
                          detectedContentType = 'email';
                        } else {
                          detectedContentType = 'freeform';
                        }
                      } else if (!detectedContentType) {
                        detectedContentType = 'freeform';
                      }
                      
                      const contentData = {
                        id: savedContentId,
                        title: draftTitle || formData.title || agentData?.name || 'Content',
                        generated_content: content,
                        ...(detectedContentType === 'email' && {
                          email_response: emailSectionsRef.current || emailSections || generatedData?.email_response,
                          email_sections: emailSectionsRef.current || emailSections || generatedData?.email_sections,
                        }),
                        ...(detectedContentType === 'blog' && {
                          blog_response: generatedData?.blog_response,
                        }),
                      };
                      
                      navigate(`/skills/diy-agent/preview/${savedContentId || 'new'}`, {
                        state: {
                          contentData,
                          contentType: detectedContentType,
                          viewMode: 'desktop',
                          agentData,
                        }
                      });
                    }}
                    disabled={isGenerating || isStreaming}
                  >
                    <BiLaptop size={20} />
                  </button> |
                  <button 
                    className="btn" 
                    onClick={() => {
                      const content = getFinalContentForSave();
                      
                      // Determine content type
                      let detectedContentType = contentType;
                      if (!detectedContentType && generatedData) {
                        if (generatedData.blog_response || generatedData.blog_response?.sections) {
                          detectedContentType = 'blog';
                        } else if (generatedData.email_response || generatedData.email_sections) {
                          detectedContentType = 'email';
                        } else {
                          detectedContentType = 'freeform';
                        }
                      } else if (!detectedContentType) {
                        detectedContentType = 'freeform';
                      }
                      
                      const contentData = {
                        id: savedContentId,
                        title: draftTitle || formData.title || agentData?.name || 'Content',
                        generated_content: content,
                        ...(detectedContentType === 'email' && {
                          email_response: emailSectionsRef.current || emailSections || generatedData?.email_response,
                          email_sections: emailSectionsRef.current || emailSections || generatedData?.email_sections,
                        }),
                        ...(detectedContentType === 'blog' && {
                          blog_response: generatedData?.blog_response,
                        }),
                      };
                      
                      navigate(`/skills/diy-agent/preview/${savedContentId || 'new'}`, {
                        state: {
                          contentData,
                          contentType: detectedContentType,
                          viewMode: 'mobile',
                          agentData,
                        }
                      });
                    }}
                    disabled={isGenerating || isStreaming}
                  >
                    <BiMobile size={20} />
                  </button>
                </div>
              </div>
            )}
 {(contentType === 'freeform' || contentType === null) && (
              <Button
                variant={isEditMode ? "warning" : "outline-primary"}
                size="sm"
                className="me-2"
                onClick={handleToggleEditMode}
                disabled={isGenerating || isStreaming}
              >
                {isEditMode ? "Cancel Edit" : "Edit Content"}
              </Button>
            )}

            {/* Save Draft button - show after content is generated */}
            {(hasFirstChunk || initialContent) && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isGenerating || isStreaming}
              >
                {isSavingDraft ? "Saving..." :  "Save as Draft"}
              </Button>
            )}
          </div>
          
          {isGenerating && !hasFirstChunk && (
            <Card className="create-form-panel">
              <Card.Body>
                <div className="text-center">
                  <Loader />
                  <p className="mt-3">Generating content...</p>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Edit Mode - Show HTML Editor (only for freeform) */}
          {isEditMode && (contentType === 'freeform' || contentType === null) ? (
            <Card className="create-form-panel">
              <Card.Body>
                <div className="mb-3">
                  <h6 className="mb-2">Edit Content</h6>
                  <p className="text-muted small mb-3">
                    You can add content manually or edit existing content. Type your HTML or plain text content in the editor below and click "Save Changes" to apply. You can start with an empty editor and add content from scratch.
                  </p>
                  <Form.Control
                    as="textarea"
                    rows={20}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    style={{ 
                      fontFamily: "monospace", 
                      fontSize: "13px",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word"
                    }}
                    placeholder="Enter your HTML content here. You can add content manually even if nothing has been generated yet..."
                  />
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={handleSaveEditedContent}
                    title="Save your content (can be empty to start fresh)"
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <>
              {hasFirstChunk && renderContent()}
              
              {/* Debug: Show if contentType is set but hasFirstChunk is false */}
              {contentType && !hasFirstChunk && (
                <div className="alert alert-warning">
                  Debug: contentType={contentType} but hasFirstChunk={hasFirstChunk.toString()}
                </div>
              )}
              
              {!hasFirstChunk && !isGenerating && (
                <StreamingContentRenderer
                  title={formData.title ? formData.title : "Generated Content"}
                  isStreaming={false}
                  isGenerating={false}
                  isTranslating={false}
                  hasFirstChunk={initialContent ? true : false}
                  isStreamingDone={initialContent ? true : false}
                  streamingContent=""
                  parsedHTMLContent={initialContent}
                  finalContent={initialContent}
                  initialContent={initialContent}
                  noContentMessage="No content generated yet. Fill in the form and click Generate to see results, or click 'Edit Content' to add content manually."
                />
              )}
            </>
          )}
        </Col>
      </Row>

      {/* Draft Modal */}
      <ContentPostDraftModal
        show={showDraftModal}
        onHide={() => setShowDraftModal(false)}
        onConfirm={handleDraftModalConfirm}
        isChecking={isSavingDraft}
        newOnly={!isContentSaved || !savedContentId}
        initialTitle={draftTitle}
      />
    </div>
  );
};

export default DiyAgentPlayground;
